import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Building2, FolderPlus, LoaderCircle, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  createCloudProject,
  createOrganization,
  loadCloudProjects,
  loadPrimaryOrganization,
  saveCloudProject,
  deleteCloudProject,
  type CloudOrganization,
  type CloudProject,
} from "../lib/cloudWorkspace";
import type { ProjectWorkspace } from "../types/domain";
import { demoWorkspace } from "../lib/projectStore";

export interface CloudControls {
  organization: CloudOrganization;
  projects: CloudProject[];
  activeProjectId: string;
  saveState: "saved" | "saving" | "error";
  userEmail: string;
  userId: string;
  onProjectChange: (projectId: string) => Promise<void>;
  onNewProject: () => void;
  onDeleteProject: () => Promise<void>;
  onSave: () => Promise<void>;
  onSignOut: () => Promise<void>;
}

export function CloudWorkspaceGate({
  children,
}: {
  children: (
    workspace: ProjectWorkspace,
    setWorkspace: (workspace: ProjectWorkspace) => void,
    controls: CloudControls,
  ) => ReactNode;
}) {
  const { user, signOut } = useAuth();
  const [organization, setOrganization] = useState<CloudOrganization | null>(null);
  const [projects, setProjects] = useState<CloudProject[]>([]);
  const [activeProjectId, setActiveProjectId] = useState("");
  const [workspace, setWorkspaceState] = useState<ProjectWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saveState, setSaveState] = useState<"saved" | "saving" | "error">("saved");
  const [showNewProject, setShowNewProject] = useState(false);
  const lastSavedRef = useRef("");
  const saveTimerRef = useRef<number | null>(null);

  const loadAccount = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError("");
    try {
      const nextOrganization = await loadPrimaryOrganization(user.id);
      setOrganization(nextOrganization);
      if (!nextOrganization) {
        setProjects([]);
        setActiveProjectId("");
        setWorkspaceState(null);
        return;
      }

      const nextProjects = await loadCloudProjects(nextOrganization.id);
      setProjects(nextProjects);
      if (nextProjects.length) {
        setActiveProjectId(nextProjects[0].id);
        setWorkspaceState(nextProjects[0].workspace);
        lastSavedRef.current = JSON.stringify(nextProjects[0].workspace);
      } else {
        setActiveProjectId("");
        setWorkspaceState(null);
        lastSavedRef.current = "";
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load the account.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  const persist = useCallback(async () => {
    if (!workspace || !activeProjectId) return true;
    const serialized = JSON.stringify(workspace);
    if (serialized === lastSavedRef.current) {
      setSaveState("saved");
      return true;
    }
    setSaveState("saving");
    try {
      const updatedAt = await saveCloudProject(activeProjectId, workspace);
      lastSavedRef.current = serialized;
      setProjects((current) =>
        current.map((project) =>
          project.id === activeProjectId
            ? { ...project, name: workspace.project.name || "Untitled project", workspace, updatedAt }
            : project,
        ),
      );
      setSaveState("saved");
      return true;
    } catch (saveError) {
      console.error("Unable to save cloud project", saveError);
      setSaveState("error");
      return false;
    }
  }, [activeProjectId, workspace]);

  useEffect(() => {
    if (!workspace || !activeProjectId) return;
    if (JSON.stringify(workspace) === lastSavedRef.current) return;
    setSaveState("saving");
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      void persist();
    }, 900);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [activeProjectId, persist, workspace]);

  function updateWorkspace(nextWorkspace: ProjectWorkspace) {
    setWorkspaceState(nextWorkspace);
  }

  async function switchProject(projectId: string) {
    if (projectId === activeProjectId) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    if (!(await persist())) return;
    const nextProject = projects.find((project) => project.id === projectId);
    if (!nextProject) return;
    setActiveProjectId(nextProject.id);
    setWorkspaceState(nextProject.workspace);
    lastSavedRef.current = JSON.stringify(nextProject.workspace);
    setSaveState("saved");
  }

  async function deleteCurrentProject() {
    if (!activeProjectId) return;
    try {
      await deleteCloudProject(activeProjectId);
      await loadAccount();
    } catch (e) {
      console.error("Failed to delete project", e);
    }
  }

  if (loading) return <CloudLoading label="Loading your secure workspace…" />;
  if (error) {
    return (
      <main className="setup-page">
        <section className="setup-card">
          <span className="eyebrow">Workspace error</span>
          <h1>We could not load your account.</h1>
          <p>{error}</p>
          <button className="button button-primary" onClick={() => void loadAccount()}>
            Try again
          </button>
        </section>
      </main>
    );
  }
  if (!user) return null;
  if (!organization) {
    return <OrganizationSetup onCreated={loadAccount} />;
  }
  if (!workspace || !projects.length) {
    return (
      <ProjectSetup
        organization={organization}
        onCreated={async (name, useDemo) => {
          const workspace = useDemo ? demoWorkspace() : undefined;
          const project = await createCloudProject(organization.id, user.id, name, workspace);
          setProjects([project]);
          setActiveProjectId(project.id);
          setWorkspaceState(project.workspace);
          lastSavedRef.current = JSON.stringify(project.workspace);
        }}
      />
    );
  }

  return (
    <>
      {children(workspace, updateWorkspace, {
        organization,
        projects,
        activeProjectId,
        saveState,
        userEmail: user.email ?? "Signed-in user",
        userId: user.id,
        onProjectChange: switchProject,
        onNewProject: () => setShowNewProject(true),
        onDeleteProject: deleteCurrentProject,
        onSave: async () => {
          await persist();
        },
        onSignOut: async () => {
          if (await persist()) await signOut();
        },
      })}
      {showNewProject && (
        <NewProjectDialog
          onClose={() => setShowNewProject(false)}
          onCreate={async (name, useDemo) => {
            if (!(await persist())) {
              throw new Error("Save the current project successfully before creating another.");
            }
            const workspace = useDemo ? demoWorkspace() : undefined;
            const project = await createCloudProject(organization.id, user.id, name, workspace);
            setProjects((current) => [project, ...current]);
            setActiveProjectId(project.id);
            setWorkspaceState(project.workspace);
            lastSavedRef.current = JSON.stringify(project.workspace);
            setSaveState("saved");
            setShowNewProject(false);
          }}
        />
      )}
    </>
  );
}

function CloudLoading({ label }: { label: string }) {
  return (
    <main className="cloud-loading" role="status">
      <span className="brand-mark">DS</span>
      <LoaderCircle className="spin" size={28} />
      <strong>{label}</strong>
    </main>
  );
}

function OrganizationSetup({ onCreated }: { onCreated: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await createOrganization(name);
      await onCreated();
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : "Unable to create the organization.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="setup-page">
      <form className="setup-card" onSubmit={submit}>
        <Building2 size={32} />
        <span className="eyebrow">First-time setup</span>
        <h1>Name your organisation or practice.</h1>
        <p>This creates the secure boundary for your projects and future team members.</p>
        <label className="setup-field">
          <span>Organisation name</span>
          <input value={name} onChange={(event) => setName(event.target.value)} minLength={2} required />
        </label>
        {error && <div className="auth-message auth-error">{error}</div>}
        <button className="button button-primary" type="submit" disabled={busy}>
          <ShieldCheck size={17} /> {busy ? "Creating…" : "Create secure workspace"}
        </button>
      </form>
    </main>
  );
}

function ProjectSetup({
  organization,
  onCreated,
}: {
  organization: CloudOrganization;
  onCreated: (name: string, useDemo?: boolean) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onCreated(name);
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : "Unable to create the project.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="setup-page">
      <form className="setup-card" onSubmit={submit}>
        <FolderPlus size={32} />
        <span className="eyebrow">{organization.name}</span>
        <h1>Create your first design-safety project.</h1>
        <p>Start with a project name. The detailed project facts come next.</p>
        <label className="setup-field">
          <span>Project name</span>
          <input value={name} onChange={(event) => setName(event.target.value)} minLength={1} required />
        </label>
        {error && <div className="auth-message auth-error">{error}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
          <button className="button button-primary" type="submit" disabled={busy}>
            <FolderPlus size={17} /> {busy ? "Creating…" : "Create project"}
          </button>
          <div style={{ textAlign: "center", margin: "0.5rem 0", color: "var(--muted)", fontSize: "0.9rem" }}>OR</div>
          <button 
            className="button button-secondary" 
            type="button" 
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                await onCreated("KL Sentral Enterprise Demo", true);
              } catch (e) {
                setError(e instanceof Error ? e.message : "Unable to load demo.");
                setBusy(false);
              }
            }}
          >
            Load KL Sentral Enterprise Demo
          </button>
        </div>
      </form>
    </main>
  );
}

function NewProjectDialog({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (name: string, useDemo?: boolean) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onCreate(name);
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : "Unable to create the project.");
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop">
      <form className="modal-card modal-compact new-project-dialog" onSubmit={submit}>
        <div className="modal-header">
          <div>
            <span className="eyebrow">Project portfolio</span>
            <h2>Start another project</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">×</button>
        </div>
        <label className="setup-field">
          <span>Project name</span>
          <input autoFocus value={name} onChange={(event) => setName(event.target.value)} required />
        </label>
        {error && <div className="auth-message auth-error">{error}</div>}
        
        <div style={{ padding: "0.5rem 0", display: "flex", justifyContent: "center" }}>
          <button 
            className="button button-secondary" 
            type="button" 
            disabled={busy}
            style={{ width: "100%", justifyContent: "center" }}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                await onCreate("KL Sentral Enterprise Demo", true);
              } catch (e) {
                setError(e instanceof Error ? e.message : "Unable to load demo.");
                setBusy(false);
              }
            }}
          >
            Load KL Sentral Enterprise Demo
          </button>
        </div>

        <div className="modal-actions">
          <button className="button button-secondary" type="button" onClick={onClose}>Cancel</button>
          <button className="button button-primary" type="submit" disabled={busy}>
            {busy ? "Creating…" : "Create project"}
          </button>
        </div>
      </form>
    </div>
  );
}
